import { normalizeTagName, useListTagsQuery } from '@vizit/api';
import { useDebounce } from '@vizit/hooks';
import { SearchSmallIcon, XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { Button, Input, SearchField } from 'react-aria-components';

const TAG_SEARCH_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 250;

function useTagSearch(workspaceId: string, search: string) {
  const name = normalizeTagName(search);
  const debouncedName = useDebounce(name, SEARCH_DEBOUNCE_MS);
  const { data, isFetching } = useListTagsQuery(
    { workspace_id: workspaceId, search: debouncedName, limit: TAG_SEARCH_LIMIT },
    { placeholderData: (previous) => previous },
  );

  return {
    name,
    tags: (data?.data ?? []).filter((tag) => tag.name.includes(name)),
    isSettled: debouncedName === name && !isFetching,
  };
}

interface TagSearchFieldProps {
  label: string;
  maxLength?: number;
}

function TagSearchField({ label, maxLength }: TagSearchFieldProps) {
  return (
    <div className="border-b border-secondary p-4">
      <SearchField aria-label={label} autoFocus className="group relative flex items-center">
        <SearchSmallIcon size={16} className="pointer-events-none absolute left-6 text-quaternary" aria-hidden />
        <Input
          placeholder={label}
          maxLength={maxLength}
          className={cx(
            'h-20 w-full rounded-xxs bg-gray-utility-50 pr-16 pl-18 text-md text-primary outline-none',
            'placeholder:text-placeholder [&::-webkit-search-cancel-button]:hidden',
          )}
        />
        <Button
          aria-label="Clear search"
          className="absolute right-4 flex h-10 w-10 items-center justify-center text-quaternary outline-none group-data-[empty]:hidden data-[hovered]:text-secondary"
        >
          <XCloseIcon size={16} aria-hidden />
        </Button>
      </SearchField>
    </div>
  );
}

export { TAG_SEARCH_LIMIT, TagSearchField, useTagSearch };
